import { Header } from "@/components/Header";
import { useSEO } from "@/hooks/use-seo";
import { Footer } from "@/components/Footer";
import { Cookie, ShieldCheck, BarChart2, Settings2, Megaphone, Info, Mail } from "lucide-react";

const COOKIE_CATEGORIES = [
  {
    icon: ShieldCheck,
    color: "#003da5",
    title: "Nezbytné soubory cookie",
    required: true,
    desc: "Tyto soubory cookie jsou nezbytné pro správné fungování webu. Umožňují základní funkce, jako je navigace mezi stránkami a přístup k zabezpečeným oblastem. Web bez těchto cookies nemůže správně fungovat.",
    examples: ["Session cookies – udržují stav vaší relace", "Bezpečnostní tokeny – chrání před CSRF útoky", "Preference uživatele – ukládají jazyk a základní nastavení"],
  },
  {
    icon: BarChart2,
    color: "#0066cc",
    title: "Analytické soubory cookie",
    required: false,
    desc: "Analytické cookies nám pomáhají pochopit, jak návštěvníci používají náš web. Veškerá data jsou anonymizována a slouží výhradně ke zlepšení obsahu a uživatelského zážitku.",
    examples: ["Google Analytics – počty návštěvníků a jejich chování", "Sledování nejnavštěvovanějších stránek a recenzí", "Analýza zdrojů návštěvnosti"],
  },
  {
    icon: Settings2,
    color: "#0088aa",
    title: "Funkční soubory cookie",
    required: false,
    desc: "Funkční cookies umožňují webu zapamatovat si vaše volby a poskytnout vám personalizovanější zážitek. Jsou nepovinné, ale zvyšují komfort používání.",
    examples: ["Uložení vašich filtrů a preferencí řazení", "Zapamatování posledně navštívených kasin", "Nastavení zobrazení obsahu"],
  },
  {
    icon: Megaphone,
    color: "#6600cc",
    title: "Marketingové soubory cookie",
    required: false,
    desc: "Marketingové cookies slouží k zobrazování relevantních reklam a sledování efektivity reklamních kampaní. Tyto soubory cookie mohou sdílet informace s třetími stranami.",
    examples: ["Affiliate sledování – pro účely provizí partnerů", "Retargetingové pixely reklamních sítí", "Měření konverzí reklamních kampaní"],
  },
];

const FAQ = [
  {
    q: "Jak mohu spravovat nebo smazat soubory cookie?",
    a: <>Soubory cookie můžete spravovat přímo v nastavení svého prohlížeče. Každý moderní prohlížeč (Chrome, Firefox, Safari, Edge) umožňuje zobrazit, smazat nebo blokovat soubory cookie pro konkrétní weby nebo globálně. Návod najdete například na stránkách podpory{" "}
      <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-chrome-cookies">Google Chrome</a>{" "}
      nebo{" "}
      <a href="https://support.mozilla.org/cs/kb/vymazani-cookies" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-firefox-cookies">Mozilla Firefox</a>.
    </>,
  },
  {
    q: "Co se stane, když cookies odmítnu?",
    a: "Odmítnutí nezbytných cookies může ovlivnit funkčnost webu. Odmítnutí analytických a marketingových cookies nebude mít vliv na váš přístup k obsahu – jen ztratíte personalizovaný zážitek.",
  },
  {
    q: "Sdílíme vaše údaje s třetími stranami?",
    a: "Analytická data jsou anonymizována. Affiliate cookie sledování sdílíme s partnerskými kasinami pro účely výplaty provizí, nikoli pro cílení reklamy. Více informací najdete v našich Zásadách ochrany osobních údajů.",
  },
  {
    q: "Jak dlouho jsou cookies uloženy?",
    a: "Nezbytné session cookies jsou smazány po zavření prohlížeče. Analytické a funkční cookies jsou zpravidla uchovávány 12 až 24 měsíců. Marketingové cookies mají dobu platnosti dle příslušného partnera, obvykle do 90 dnů.",
  },
];

export default function ZasadyCookiesPage() {
  useSEO({ title: "Zásady Cookies – Ochrana soukromí | HrajBonus", description: "Informace o používání souborů cookie na webu HrajBonus. Zjistěte, jaká data shromažďujeme, proč a jak je spravovat nebo odmítnout.",
  url: "https://hrajbonus.com/zasady-cookies", });

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      <Header />

      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 25% 50%, white 1px, transparent 1px), radial-gradient(circle at 75% 50%, white 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 sm:py-20 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-white" />
            </div>
            <span className="text-white/60 text-sm font-semibold uppercase tracking-widest">Soubory cookie</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5 max-w-2xl">
            Zásady používání souborů cookie
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl">
            Na hrajbonus.com používáme soubory cookie, abychom vám zajistili co nejlepší zážitek při procházení webu. Tato stránka vysvětluje, jaké cookies používáme, proč je používáme a jak je spravovat. Zpracování cookies probíhá v souladu s doporučeními{" "}
            <a href="https://www.uoou.gov.cz/" target="_blank" rel="noopener noreferrer" className="underline text-white/80 hover:text-white" data-testid="link-uoou-cookies">Úřadu pro ochranu osobních údajů (ÚOOÚ)</a>.
          </p>
          <p className="text-white/40 text-sm mt-4">Naposledy aktualizováno: leden 2026</p>
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-20 flex-1 space-y-16">

        <section>
          <div className="bg-white rounded-3xl border border-[#dde4ee] shadow-sm p-8 sm:p-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Info className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Co jsou soubory cookie?</h2>
                <p className="text-gray-600 leading-relaxed">
                  Soubory cookie jsou malé textové soubory, které se ukládají do vašeho zařízení (počítač, tablet, telefon) při návštěvě webové stránky. Umožňují webu zapamatovat si vaše akce a preference po určitou dobu, takže je nemusíte znovu zadávat při každé návštěvě nebo při přechodu z jedné stránky na druhou.
                </p>
                <p className="text-gray-600 leading-relaxed mt-3">
                  Soubory cookie neobsahují viry ani škodlivý software a neposkytují přístup k vašemu zařízení. Jsou to pouze malé datové soubory, které slouží ke zlepšení vašeho uživatelského zážitku a pomáhají nám pochopit, jak návštěvníci náš web používají.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Cookie className="w-3.5 h-3.5" /> Kategorie cookies
            </span>
            <h2 className="text-3xl font-black text-gray-900">Jaké cookies používáme</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {COOKIE_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              return (
                <div key={cat.title} className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: cat.color }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-gray-900">{cat.title}</h3>
                        {cat.required && (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white" style={{ background: "#003da5" }}>Povinné</span>
                        )}
                        {!cat.required && (
                          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Volitelné</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{cat.desc}</p>
                  <ul className="space-y-1.5">
                    {cat.examples.map((ex) => (
                      <li key={ex} className="flex items-start gap-2 text-xs text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#003da5] flex-shrink-0 mt-1.5" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Settings2 className="w-3.5 h-3.5" /> Správa cookies
            </span>
            <h2 className="text-3xl font-black text-gray-900 mb-4">Jak spravovat soubory cookie</h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl">
              Máte plnou kontrolu nad tím, jaké soubory cookie přijmete. Níže najdete odpovědi na nejčastější otázky ohledně správy cookies.
            </p>
          </div>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="bg-[#f3f6fb] rounded-3xl border border-[#dde4ee] p-8 sm:p-12 text-center">
            <h2 className="text-2xl font-black text-gray-900 mb-3">Máte otázky?</h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Pokud máte jakékoli dotazy ohledně našich zásad používání cookies nebo chcete uplatnit svá práva, neváhejte nás kontaktovat.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="mailto:info@aroundcasinos.com"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #003da5, #002d7c)", boxShadow: "0 4px 14px rgba(0,61,165,0.25)" }}
                data-testid="link-cookie-email"
              >
                <Mail className="w-4 h-4" /> info@aroundcasinos.com
              </a>
              <a
                href="/zasady-ochrany-osobnich-udaju"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-[#003da5] bg-white border border-[#dde4ee] hover:border-[#003da5] transition-all"
                data-testid="link-privacy-policy"
              >
                Zásady ochrany osobních údajů
              </a>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
